import find from 'lodash/find';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import { Excludes, NumberOperator, NumericalOperatorCondition } from 'src/actions/rules/condition';
import NumberInputField from './NumberInputField';
import {
  StyledConditionLabel,
  StyledDropdown,
  StyledInformationIcon,
  StyledTooltip,
} from 'src/presentation/components/pages/rules/shared/StyledComponents';

export type Option = {
  label: string;
  value: string;
};

type NumericConditionSectionProps = {
  additionalOperators?: Option[];
  condition: NumericalOperatorCondition;
  index: number;
  isReadOnly?: boolean;
  limitDecimals?: number;
  convertToDisplayFn?: Function;
  convertFromDisplayFn?: Function;
  onConditionOperatorChange?: Function;
  onUpdate: Function;
  showExclusion?: boolean;
  showValidationTooltip?: boolean;
  unitLabel?: string;
  excludedOperators?: string[];
  requirePositive?: boolean;
  inputProps?: any;
};

const NumericConditionSection: FC<NumericConditionSectionProps> = ({
  isReadOnly,
  condition,
  index,
  limitDecimals,
  convertToDisplayFn,
  convertFromDisplayFn,
  onConditionOperatorChange,
  onUpdate,
  showExclusion = true,
  showValidationTooltip = false,
  unitLabel,
  excludedOperators = [],
  requirePositive = false,
  inputProps = {},
  additionalOperators = [],
}) => {
  const onBeginChange = useCallback(
    val => {
      if (requirePositive && (val < 0 || isNaN(val))) return;

      condition.setBegin(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate, requirePositive]
  );

  const onEndChange = useCallback(
    val => {
      if (requirePositive && (val < 0 || isNaN(val))) return;

      condition.setEnd(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate, requirePositive]
  );

  const onExclusionChange = useCallback(
    val => {
      condition.setNot(val === Excludes.True);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onOperatorChange = useCallback(
    val => {
      if (onConditionOperatorChange) {
        onConditionOperatorChange(val);
      } else {
        condition.setOperator(val);
        onUpdate({ condition, index });
      }
    },
    [condition, index, onConditionOperatorChange, onUpdate]
  );

  const onValueChange = useCallback(
    val => {
      if (requirePositive && (val < 0 || isNaN(val))) return;

      condition.setValue(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate, requirePositive]
  );

  const onKeyDown = useCallback(
    evt => {
      if (requirePositive) {
        // Prevent negative and positive signs
        if (evt.key === '-' || evt.key === '+') {
          evt.preventDefault();
          return;
        }
      }
    },
    [requirePositive]
  );

  const availableOperators = numberOperatorOptions.filter(option => !excludedOperators.includes(option.value));
  if (additionalOperators.length) {
    availableOperators.push(...additionalOperators);
  }

  if (isReadOnly) {
    return (
      <>
        {showExclusion && (
          <ExclusionLabel data-aid="readOnlyExclusionValue">
            {renderExclusionLabel(condition.not ? Excludes.True : Excludes.False)}
          </ExclusionLabel>
        )}
        <StyledConditionLabel data-aid="readOnlyOperator">
          {renderOperatorLabel(availableOperators, condition.getOperator())}
        </StyledConditionLabel>
        {renderReadOnlyValue()}
      </>
    );
  }

  return (
    <>
      {showExclusion && (
        <ExclusionDropdown
          data-aid="numericSectionExclusionDropdown"
          onSelect={onExclusionChange}
          options={exclusionOptions}
          value={condition.not ? Excludes.True : Excludes.False}
        />
      )}
      <NumberOperatorDropdown
        data-aid="numericSectionOperatorDropdown"
        onSelect={onOperatorChange}
        options={availableOperators}
        value={condition.getOperator()}
      />
      {renderOperands()}
    </>
  );

  function renderOperands() {
    const operator = condition.getOperator();
    if (!numberOperatorOptionsValues.includes(operator)) {
      return null;
    }

    const commonInputProps = {
      attr: `conditions[${index}].value`,
      'data-aid': 'numericSectionValue',
      limitDecimals,
      convertToDisplayFn,
      convertFromDisplayFn,
      ...(requirePositive && { onKeyDown }),
      ...inputProps,
    };

    if (operator === NumberOperator.Between) {
      const beginProps = {
        attr: `conditions[${index}].begin`,
        'data-aid': 'numericSectionBegin',
        limitDecimals,
        convertToDisplayFn,
        convertFromDisplayFn,
        ...(requirePositive && { onKeyDown }),
        ...inputProps,
      };

      const endProps = {
        attr: `conditions[${index}].end`,
        'data-aid': 'numericSectionEnd',
        limitDecimals,
        convertToDisplayFn,
        convertFromDisplayFn,
        ...(requirePositive && { onKeyDown }),
        ...inputProps,
      };

      return (
        <StyledOperands>
          <BetweenOperandInputField>
            <NumberInputField {...beginProps} onUpdate={onBeginChange} />
          </BetweenOperandInputField>
          <span data-aid="numericSectionOperandSeparator">And</span>
          <BetweenOperandInputField>
            <NumberInputField {...endProps} onUpdate={onEndChange} />
          </BetweenOperandInputField>
          {showValidationTooltip && (
            <div>
              <ValidationTooltip requirePositive={requirePositive} />
            </div>
          )}
          {unitLabel && <StyledUnitLabel>{unitLabel}</StyledUnitLabel>}
        </StyledOperands>
      );
    }

    return (
      <StyledOperands>
        <ValueInputField>
          <NumberInputField {...commonInputProps} onUpdate={onValueChange} />
        </ValueInputField>
        {showValidationTooltip && (
          <div>
            <ValidationTooltip requirePositive={requirePositive} />
          </div>
        )}
        {unitLabel && <StyledUnitLabel>{unitLabel}</StyledUnitLabel>}
      </StyledOperands>
    );
  }

  function renderExclusionLabel(value: string) {
    const exclusion = find(exclusionOptions, { value });
    return exclusion ? exclusion.label : '';
  }

  function renderOperatorLabel(availableOperators: Option[], value: string) {
    const operator = find(availableOperators, { value });
    return operator ? operator.label : '';
  }

  function renderReadOnlyValue() {
    const operator = condition.getOperator();
    if (!numberOperatorOptionsValues.includes(operator)) {
      return null;
    }

    if (operator === NumberOperator.Between) {
      return (
        <>
          <ValueLabel data-aid="readOnlyBegin">
            {convertToDisplayFn ? convertToDisplayFn(condition.begin) : condition.begin}
          </ValueLabel>
          <BetweenOperandSeparator data-aid="readOnlyOperandSeparator">And</BetweenOperandSeparator>
          <ValueLabel data-aid="readOnlyEnd">
            {convertToDisplayFn ? convertToDisplayFn(condition.end) : condition.end}
          </ValueLabel>
          {unitLabel && <StyledUnitLabel>{unitLabel}</StyledUnitLabel>}
        </>
      );
    }

    return (
      <>
        <ValueLabel data-aid="readOnlyValue">
          {convertToDisplayFn ? convertToDisplayFn(condition.value) : condition.value}
        </ValueLabel>
        {unitLabel && <StyledUnitLabel>{unitLabel}</StyledUnitLabel>}
      </>
    );
  }
};

const ValidationTooltip: FC<{ requirePositive: boolean }> = ({ requirePositive }) => (
  <StyledTooltip message={requirePositive ? 'Value must be a positive number' : 'Value must be a valid number'}>
    <StyledInformationIcon />
  </StyledTooltip>
);

const BetweenOperandSeparator = styled.span`
  margin-right: ${p => p.theme.spacing.medium};
`;

const StyledOperands = styled.div`
  align-items: center;
  display: flex;
  flex: 0;
`;

const StyledUnitLabel = styled.div`
  display: flex;
  flex: 1;
`;

const BetweenOperandInputField = styled.div`
  margin: 0 6px;
  max-width: 60px;
  width: 60px;

  .betweenOperandError {
    display: none;
  }

  &&& input {
    width: 40px;
  }
`;

const ExclusionDropdown = styled(StyledDropdown)`
  width: 90px;
`;

const ExclusionLabel = styled.span`
  margin-right: ${p => p.theme.spacing.medium};
`;

const ValueLabel = styled.span`
  margin-right: ${p => p.theme.spacing.medium};
`;

const NumberOperatorDropdown = styled(StyledDropdown)`
  margin-left: 4px;
  width: 140px;
`;

const ValueInputField = styled.div`
  margin: 0 8px;
  max-width: 120px;

  &&& input {
    width: 100px;
  }
`;

// dropdown selection does not render when using false as a boolean
const exclusionOptions: Option[] = [
  { label: 'is', value: Excludes.False },
  { label: 'is not', value: Excludes.True },
];

export const numberOperatorOptions: Option[] = [
  { label: 'Between', value: NumberOperator.Between },
  { label: 'Equal to', value: NumberOperator.Equal },
  { label: 'Greater than', value: NumberOperator.GT },
  { label: 'Greater or equal', value: NumberOperator.GTE },
  { label: 'Less than', value: NumberOperator.LT },
  { label: 'Less or equal', value: NumberOperator.LTE },
];
const numberOperatorOptionsValues: string[] = numberOperatorOptions.map(option => option.value);

export default NumericConditionSection;
